import type { DocusignSignatureResponse } from '@/services/proposals/types';
import {
  mapEnvelopeStatusLabel,
  mapRecipientStatusLabel,
} from '@/features/proposals/utils/docusign-signature-status.util';
import { Button, Text } from '@noorden-insurtech/alba-design-system';

type RecipientRowProps = {
  role: string;
  name: string;
  email: string;
  status: string;
};

function RecipientStatusRow({ role, name, email, status }: RecipientRowProps) {
  return (
    <div className='flex flex-col gap-1 rounded-xl border border-alba-gray-200 bg-alba-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
      <div className='flex flex-col gap-0.5 text-left'>
        <Text className='text-xs font-medium uppercase tracking-wide text-alba-gray-500'>
          {role}
        </Text>
        <Text className='text-sm font-semibold text-alba-gray-900'>{name}</Text>
        <Text className='text-xs text-alba-gray-600'>{email}</Text>
      </div>
      <Text className='text-sm font-medium text-alba-gray-800'>
        {mapRecipientStatusLabel(status)}
      </Text>
    </div>
  );
}

type DocumentCardProps = {
  title: string;
  signature: DocusignSignatureResponse;
  canRetry: boolean;
  isRetryPending: boolean;
  onRetry: () => void;
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
};

function PmeDocumentSignatureCard({
  title,
  signature,
  canRetry,
  isRetryPending,
  onRetry,
  actionsDisabled = false,
  actionsDisabledReason,
}: DocumentCardProps) {
  const isBrokerOnly = signature.signerLayout === 'broker_only';

  return (
    <div className='flex flex-col gap-4 rounded-2xl border border-alba-gray-200 bg-white p-6 shadow-sm'>
      <div className='flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between'>
        <div className='flex flex-col gap-1'>
          <Text className='text-lg font-semibold text-alba-gray-900'>{title}</Text>
          <Text className='text-sm text-alba-gray-600'>
            {mapEnvelopeStatusLabel(signature.status, signature.failureSubtype)}
          </Text>
        </div>
        {(canRetry) && (
          <div className='flex flex-wrap gap-2 pt-2 sm:pt-0'>
            <Button
              type='button'
              size='sm'
              variant='primary'
              onClick={onRetry}
              disabled={isRetryPending || actionsDisabled}
              title={actionsDisabledReason}
              loading={isRetryPending}
            >
              Tentar novamente
            </Button>
          </div>
        )}
      </div>

      {!isBrokerOnly && (
        <RecipientStatusRow
          role='Estipulante'
          name={signature.stipulatorName}
          email={signature.stipulatorEmail}
          status={signature.stipulatorStatus}
        />
      )}

      <RecipientStatusRow
        role='Corretor'
        name={signature.brokerName}
        email={signature.brokerEmail}
        status={signature.brokerStatus}
      />

      {signature.status === 'completed' && (
        <Text className='text-sm text-alba-green-700'>
          Documento assinado e registrado na proposta.
        </Text>
      )}
    </div>
  );
}

type Props = {
  proposalEnvelope: DocusignSignatureResponse | null;
  commercialLetterEnvelope: DocusignSignatureResponse | null;
  canRetryProposal: boolean;
  canRetryCommercialLetter: boolean;
  canSyncAny: boolean;
  isSyncPending: boolean;
  isRetryPending: boolean;
  onSyncAll: () => void;
  onRetry: (kind: 'proposal' | 'commercial_letter') => void;
  /** Trava externa (ex.: corretora inativa): desabilita com o motivo no title. */
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
  /** Aviso abaixo do título (ex.: assinaturas concluídas aguardando a transmissão). */
  notice?: string;
};

export function PmeProposalSignaturesSection({
  proposalEnvelope,
  commercialLetterEnvelope,
  canRetryProposal,
  canRetryCommercialLetter,
  canSyncAny,
  isSyncPending,
  isRetryPending,
  onSyncAll,
  onRetry,
  actionsDisabled = false,
  actionsDisabledReason,
  notice,
}: Props) {
  if (!proposalEnvelope && !commercialLetterEnvelope) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-col gap-1'>
          <Text className='text-xl font-semibold text-alba-gray-900'>
            Assinaturas digitais
          </Text>
          <Text className='text-sm text-alba-gray-600'>
            Acompanhe o status de assinatura dos documentos enviados via DocuSign.
          </Text>
          {notice && (
            <Text className='text-sm font-medium text-alba-green-700'>
              {notice}
            </Text>
          )}
        </div>
        {canSyncAny && (
          <Button
            type='button'
            size='sm'
            variant='secondary'
            onClick={onSyncAll}
            disabled={isSyncPending || isRetryPending || actionsDisabled}
            title={actionsDisabledReason}
            loading={isSyncPending}
            className='mt-2 sm:mt-0'
          >
            Atualizar todos
          </Button>
        )}
      </div>

      {proposalEnvelope && (
        <PmeDocumentSignatureCard
          title='Proposta'
          signature={proposalEnvelope}
          canRetry={canRetryProposal}
          isRetryPending={isRetryPending}
          onRetry={() => onRetry('proposal')}
          actionsDisabled={actionsDisabled}
          actionsDisabledReason={actionsDisabledReason}
        />
      )}

      {commercialLetterEnvelope && (
        <PmeDocumentSignatureCard
          title='Carta Comercial'
          signature={commercialLetterEnvelope}
          canRetry={canRetryCommercialLetter}
          isRetryPending={isRetryPending}
          onRetry={() => onRetry('commercial_letter')}
          actionsDisabled={actionsDisabled}
          actionsDisabledReason={actionsDisabledReason}
        />
      )}
    </div>
  );
}
